/* eslint-disable no-undef */
/* eslint-disable no-underscore-dangle */
import {
    YMaps, Map, Placemark,
} from '@pbe/react-yandex-maps';
import {
    Button, Form, Input, Modal, Select, Space, Spin,
} from 'antd';
import { produce } from 'immer';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import YandexGeocoder from '../../services/yandex-geocoder';
import { IFormItemProps } from '../../factories/form';
import { useGeolocationAddress } from '../../hooks/useGeolocationAddress';
import { useYandexMapToken } from '../../hooks/useYandexMapToken';

interface IAddress {
    address: {
        amenity: string;
        house_number: number;
        road: string;
        city_district: string;
        city: string;
        state: string;
        country: string;
        suburb: string;
        town: string;
    };
    category: string;
    display_name: string;
    lat: string;
    lon: string;
    place_id: number;
}

function GeolocationAddress({ formRef, formDataLoaded }: IFormItemProps) {
    const [address, setAddress] = useState<string | undefined>();
    const [addresses, setAddresses] = useState<IAddress[]>([]);
    const [coords, setCoords] = useState<[number, number] | undefined>();
    const [visible, setVisible] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [searching, setSearching] = useState(false);
    const { center, loading } = useGeolocationAddress();
    const yandexMap = useYandexMapToken();
    const canSearch = useRef(true);
    const searchTimeout = useRef<NodeJS.Timeout>();
    const mapRef = useRef<ymaps.Map>();

    const processGeolocationAddress = async (geolocation: [number, number]) => {
        if (typeof yandexMap.tokens?.geocoder === 'string') {
            try {
                const result = await YandexGeocoder.getAddress(
                    produce(geolocation, (draft: [number, number]) => { draft.reverse(); }),
                    yandexMap.tokens.geocoder,
                );
                const { text } = result.response.GeoObjectCollection.featureMember[0].GeoObject.metaDataProperty.GeocoderMetaData;
                if (typeof text === 'string') {
                    setAddress(text);
                    formRef.setFieldsValue({
                        address: text,
                    });
                }
            } catch (err) {
                setAddress('Ошибка поиска');
            }
        }
    };

    const searchAddress = async (value: string) => {
        try {
            const response = await axios({
                url: `https://nominatim.openstreetmap.org/search?q=${value}&accept-language=ru-RU&format=jsonv2&layer=address&addressdetails=1`,
            });
            // setAddresses(response.data.filter((el: IAddress) => el.category === 'building'));
            setAddresses(response.data);
        } catch {
            setAddresses([]);
        } finally {
            setSearching(false);
        }
    };

    useEffect(() => {
        if (Array.isArray(coords)) {
            processGeolocationAddress(coords);
            formRef.setFieldsValue({
                latitude: typeof coords?.[0] === 'number' ? coords[0].toString() : undefined,
                longitude: typeof coords?.[1] === 'number' ? coords[1].toString() : undefined,
            });
        }
    }, [coords]);

    useEffect(() => {
        if (formDataLoaded) {
            const addressText = formRef.getFieldValue('address');
            const latitude = formRef.getFieldValue('latitude');
            const longitude = formRef.getFieldValue('longitude');

            if (typeof addressText === 'string') {
                setAddress(addressText);
            }

            if (latitude && longitude) {
                setCoords([latitude, longitude]);
            }
        }
    }, [formDataLoaded]);

    useEffect(() => {
        if (canSearch.current) {
            // @ts-ignore
            clearTimeout(searchTimeout.current);
            if (typeof searchValue === 'string' && searchValue.length > 3) {
                setSearching(true);
                searchTimeout.current = setTimeout(() => {
                    searchAddress(searchValue);
                }, 1500);
            } else {
                setAddresses([]);
                setSearching(false);
            }
        }
    }, [searchValue]);

    const getAddressLabel = (item: IAddress) => [
        item.address.country,
        item.address.state,
        item.address.city,
        item.address.city_district,
        item.address.town,
        item.address.suburb,
        item.address.road,
        item.address.house_number,
        item.address.amenity,
    ].filter((el) => typeof el === 'string').join(', ');

    const onBtnClick = async () => {
        await yandexMap.getTokens();
        setVisible(true);
    };

    const onClose = () => setVisible(false);

    const onMapClick = (e: any) => {
        setAddress(undefined);
        setCoords(e.get('coords'));
    };

    const onInstanceRef = (ref: any) => {
        if (ref) {
            mapRef.current = ref;
            ref?.events?.add('click', onMapClick);
        }
    };

    const onSearch = (value: string) => {
        if (searchValue.length > 1 && value.length === 0) {
            return null;
        }
        canSearch.current = true;
        return setSearchValue(value);
    };

    const onSelect = (value: number) => {
        const [item] = addresses.filter((el) => el.place_id === value);
        if (item instanceof Object) {
            canSearch.current = false;
            setSearchValue(getAddressLabel(item));
            const selectedCoords = [
                Number(item.lat),
                Number(item.lon),
            ];
            // @ts-ignore
            setCoords(selectedCoords);
            mapRef.current?.setCenter(selectedCoords, 16);
        }
    };

    return (
        <>
            <Space direction="vertical" style={{ width: '100%' }}>
                {address && <Input.TextArea readOnly value={address} />}
                <Button onClick={onBtnClick} type="primary" loading={yandexMap.loading}>Указать адрес</Button>
            </Space>
            <Form.Item name="address" hidden />
            <Form.Item name="latitude" hidden />
            <Form.Item name="longitude" hidden />
            <Modal visible={visible} onCancel={onClose} width="80%" onOk={onClose} title="Выбор адреса">
                <Space className="w-100" direction="vertical">
                    <Select
                        filterOption={false}
                        searchValue={searchValue}
                        placeholder="Введите адрес"
                        showSearch
                        onSearch={onSearch}
                        loading={searching}
                        options={addresses.map((el) => ({
                            label: getAddressLabel(el),
                            value: el.place_id,
                        }))}
                        onSelect={onSelect}
                    />
                    <Spin className="w-100" spinning={loading}>
                        <YMaps query={{ apikey: yandexMap.tokens?.geocoder }}>
                            <Map
                                key={center[0]}
                                instanceRef={onInstanceRef}
                                defaultState={{ center, zoom: 9 }}
                                width="100%"
                                height="65vh"
                            >
                                {Array.isArray(coords) && (
                                    <Placemark
                                        geometry={coords}
                                        options={{ preset: 'islands#violetDotIconWithCaption' }}
                                        properties={{ iconCaption: address || 'Поиск...' }}
                                    />
                                )}
                            </Map>
                        </YMaps>
                    </Spin>
                </Space>
            </Modal>
        </>
    );
}

export default GeolocationAddress;
